// Bailey, asked in words: "20% off balayage this week", "free slots tomorrow
// at 2 and 4", "meet Jess, our new colour specialist", "headline: New season,
// new you", "pink background", "post it Friday at 6pm". Works out what was
// asked, then does it: the kind of post, its words, its day and times, its
// colours and size, when to post it, the caption.
//
// Literal on purpose. It only uses what the salon typed and their own diary,
// menu, vouchers, team and reviews (passed in as ctx), so nothing is made up.
// A new post keeps none of the template's example facts, and Bailey says what
// changed and what's still needed. Words about the look ("bolder", "festive")
// go to the remix, which offers versions to choose from.
//
// No lookbehind in any pattern here: older iPhone Safari can't parse them.
import moment from 'moment'
import { normHex, mix } from '../../email-composer/email/theme'
import { fixColours, hardToRead } from '../render/colourways'
import { TEXT_SIZES, postTheme } from '../render/render'
import { POST_TYPES, changeType, makePost, showcaseFor, isStock, layoutOf, EXAMPLE_FACTS } from './postTypes'
import { isoDay } from './dates'
import { linesFor } from './phrases'
import { understand, COLOUR_WORDS } from './remix'

const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim()
const cap = (s) => { const t = clean(s); return t ? t.charAt(0).toUpperCase() + t.slice(1) : '' }
const lower = (s) => { const t = clean(s); return t ? t.charAt(0).toLowerCase() + t.slice(1) : '' }
const sentence = (s) => { const t = cap(s); return !t ? '' : /[.!?…]$/.test(t) ? t : `${t}.` }
const plain = (s) => String(s || '').replace(/\*/g, '')
// Typed apostrophes become the typographic kind the designs use
const typo = (s) => clean(s).replace(/'/g, '’')
const pounds = (n) => { const v = Math.round(Number(n) * 100) / 100; return Number.isInteger(v) ? `£${v}` : `£${v.toFixed(2)}` }
const firstName = (s) => clean(s).split(' ')[0] || ''
const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const timeLabel = (mins) => {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`
}
export const andList = (list) => (list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`)
// Sentences, each keeping its own full stop
const sentences = (text) => {
  const out = []
  const parts = String(text || '').split(/([.!?]+)\s+/)
  for (let i = 0; i < parts.length; i += 2) {
    const s = clean(parts[i] + (parts[i + 1] || ''))
    if (/[a-z0-9]/i.test(s)) out.push(s)
  }
  return out
}
// Smaller pieces: "make it pink, and give it a playful caption"
const clauses = (text) => sentences(text).flatMap(s => s.split(/,\s*|;\s*|\s+and\s+|\s+then\s+/i)).map(clean).filter(s => /[a-z0-9]/i.test(s))
// Text with a phrase cut out, and the punctuation left behind tidied
const tidy = (s) => clean(s).replace(/\s+([,.;!?])/g, '$1').replace(/([,;])(\s*[,;])+/g, '$1').replace(/[,;]\s*([.!?])/g, '$1').replace(/^[,.;!?\s]+/, '').replace(/[,;]\s*$/, '')

// ─── Days ───
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
const DAY_FULL = '(?:mon|tues|wednes|thurs|fri|satur|sun)days?'
const DAY_SHORT = '(?:mon|tues?|wed|thu(?:rs?)?|fri|sat|sun)'
const DAY_ANY = `(?:${DAY_FULL}|${DAY_SHORT})`
const MONTH_NAMES = '(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept?|oct|nov|dec)'
const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 }
const dayIndex = (word) => {
  const w = String(word || '').toLowerCase().slice(0, 3)
  return w.length === 3 ? WEEKDAYS.findIndex(d => d.startsWith(w)) : -1
}
const dayName = (word) => cap(WEEKDAYS[dayIndex(word)] || '')
const isFullDay = (word) => new RegExp(`^${DAY_FULL}$`, 'i').test(word)
const upcoming = (today, idx) => moment(today).add((idx - today.day() + 7) % 7, 'days')
// "Next Friday" is the Friday of next week
const nextWeek = (today, idx) => moment(today).startOf('isoWeek').add(1, 'week').isoWeekday(idx === 0 ? 7 : idx)
const RANGE = `(${DAY_ANY})\\s*(?:-|–|to|until|till|thru|through)\\s*(${DAY_ANY})(?![a-z-])`

// Every day mentioned: [{ date: moment, index, phrase }]. Full day names
// anywhere; short ones ("Thu") after on / this / next / until, in a range, or
// anywhere with `loose` (a "post it..." clause), so "sun-kissed" or "sat in
// the chair" never read as days.
export const findDays = (text, now = new Date(), { loose = false } = {}) => {
  const out = []
  const today = moment(now).startOf('day')
  const add = (d, index, phrase) => { if (d && d.isValid()) out.push({ date: d.startOf('day'), index, phrase }) }
  let m
  const rel = /\b(today|tonight|tomorrow|tmrw|tomoz)\b/gi
  while ((m = rel.exec(text))) add(/^to(?:day|night)$/i.test(m[1]) ? moment(today) : moment(today).add(1, 'day'), m.index, m[0])
  // A range ("Tue-Thu") is both its days, the second on or after the first
  const covered = new Set()
  const range = new RegExp(`\\b${RANGE}`, 'gi')
  while ((m = range.exec(text))) {
    const a = dayIndex(m[1])
    const b = dayIndex(m[2])
    if (a < 0 || b < 0) continue // eslint-disable-line no-continue
    const first = upcoming(today, a)
    const at2 = m.index + m[0].length - m[2].length
    add(first, m.index, m[1])
    add(moment(first).add((b - a + 7) % 7, 'days'), at2, m[2])
    covered.add(m.index)
    covered.add(at2)
  }
  const wd = new RegExp(`\\b(?:(this|next|on|until|till|til|to|from|every|by|for)\\s+)?(${DAY_ANY})(?![a-z-])`, 'gi')
  while ((m = wd.exec(text))) {
    const word = m[2]
    if (covered.has(m.index + m[0].length - word.length)) continue // eslint-disable-line no-continue
    if (!isFullDay(word) && !m[1] && !loose) continue // eslint-disable-line no-continue
    const idx = dayIndex(word)
    if (idx < 0) continue // eslint-disable-line no-continue
    add((m[1] || '').toLowerCase() === 'next' ? nextWeek(today, idx) : upcoming(today, idx), m.index, m[0])
  }
  const onDate = (mon, day) => {
    const d = moment(today).month(mon).date(day)
    if (d.month() !== mon) return null
    return d.isBefore(today) ? d.add(1, 'year') : d
  }
  const dm = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH_NAMES}\\b`, 'gi')
  while ((m = dm.exec(text))) add(onDate(MONTHS[m[2].toLowerCase().slice(0, 3)], Number(m[1])), m.index, m[0])
  const md = new RegExp(`\\b${MONTH_NAMES}\\s+(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'gi')
  while ((m = md.exec(text))) add(onDate(MONTHS[m[1].toLowerCase().slice(0, 3)], Number(m[2])), m.index, m[0])
  const slash = /\b(\d{1,2})\/(\d{1,2})\b/g
  while ((m = slash.exec(text))) {
    const day = Number(m[1])
    const mon = Number(m[2]) - 1
    if (day >= 1 && day <= 31 && mon >= 0 && mon <= 11) add(onDate(mon, day), m.index, m[0])
  }
  return out.sort((a, b) => a.index - b.index)
}

// ─── Times ───
// "2pm", "2:30pm", "14:00", "2.30pm", and bare hours after "at" ("at 2 and
// 4"). Never a price (£10.50) or a discount (20%).
export const findTimes = (text) => {
  const out = []
  const seen = new Set()
  const add = (h, min, ap, index) => {
    let hour = Number(h)
    const mins = Number(min || 0)
    const a = (ap || '').toLowerCase()
    if (a === 'pm' && hour < 12) hour += 12
    if (a === 'am' && hour === 12) hour = 0
    // A bare hour in a salon: 8 to 11 is the morning, 1 to 7 the afternoon
    if (!a && hour >= 1 && hour <= 7) hour += 12
    if (hour > 23 || mins > 59) return
    const total = hour * 60 + mins
    if (seen.has(total)) return
    seen.add(total)
    out.push({ mins: total, label: timeLabel(total), index })
  }
  const priced = (i) => i > 0 && /[£$€\d.:]/.test(text.charAt(i - 1))
  let m
  const explicit = /\b(\d{1,2})(?:[:.](\d{2}))?\s?(am|pm)\b/gi
  while ((m = explicit.exec(text))) if (!priced(m.index)) add(m[1], m[2], m[3], m.index)
  const clock = /\b(\d{1,2})[:.](\d{2})\b(?!\s?(?:am|pm|%))/gi
  while ((m = clock.exec(text))) if (!priced(m.index)) add(m[1], m[2], '', m.index)
  const at = /\bat\s+((?:\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm)?\s*(?:,|and|&|or)?\s*)+)(?![\d%£]|\s*(?:%|off|percent))/gi
  while ((m = at.exec(text))) {
    const chunk = m[1]
    const start = m.index + m[0].indexOf(chunk)
    const each = /(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?/gi
    let t
    while ((t = each.exec(chunk))) add(t[1], t[2], t[3], start + t.index)
  }
  return out.sort((a, b) => a.index - b.index)
}

// A part of the day, as minutes: "tomorrow morning", "tonight"
const dayPart = (text) => {
  if (/\bmorning\b/i.test(text)) return 9 * 60
  if (/\b(?:noon|midday)\b/i.test(text)) return 12 * 60
  if (/\blunch(?:time)?\b/i.test(text)) return 12 * 60 + 30
  if (/\bafternoon\b/i.test(text)) return 15 * 60
  if (/\b(?:evening|tonight|night)\b/i.test(text)) return 19 * 60
  return null
}
// When a clause says to post: a day and a time (12:30pm when only a day's
// given; a time on its own that's gone today means tomorrow)
const whenFrom = (clause, now, { loose = false } = {}) => {
  const days = findDays(clause, now, { loose })
  const times = findTimes(clause)
  const part = dayPart(clause)
  if (!days.length && !times.length && part == null) return null
  const day = days.length ? moment(days[0].date) : moment(now).startOf('day')
  const mins = times.length ? times[0].mins : part != null ? part : 12 * 60 + 30
  let at = moment(day).startOf('day').add(mins, 'minutes')
  if (!days.length && at.isBefore(moment(now))) at = at.add(1, 'day')
  return at.toDate()
}

// ─── Money ───
const findMoney = (text) => {
  const pct = /(\d{1,3})\s?(?:%|percent|per cent)\s?off\b/i.exec(text) || /\bsave\s+(\d{1,3})\s?(?:%|percent|per cent)/i.exec(text)
  const offPounds = /£\s?(\d+(?:\.\d{2})?)\s?off\b/i.exec(text) || /\bsave\s+£\s?(\d+(?:\.\d{2})?)/i.exec(text)
  const half = /\bhalf[\s-]?price\b|\bhalf\s+off\b/i.exec(text)
  const twoForOne = /\b(?:2|two)\s*(?:for|4)\s*(?:1|one)\b|\bbogof\b|\bbuy one,? get one free\b/i.exec(text)
  const from = /\bfrom\s+(?:just\s+|only\s+)?£\s?(\d+(?:\.\d{2})?)/i.exec(text)
  const wasNow = /\bwas\s+£\s?(\d+(?:\.\d{2})?)[,;\s]+(?:now\s+)?(?:just\s+|only\s+)?£\s?(\d+(?:\.\d{2})?)/i.exec(text)
  const usual = /\b(?:usually|normally|full price(?:\s+is)?|rrp|was)\s*:?\s*£\s?(\d+(?:\.\d{2})?)/i.exec(text)
  const hit = pct || offPounds || half || twoForOne
  return {
    discount: pct ? `${Number(pct[1])}% off` : offPounds ? `${pounds(offPounds[1])} off` : half ? 'Half price' : twoForOne ? '2 for 1' : null,
    percent: pct ? Number(pct[1]) : half ? 50 : null,
    off: !pct && offPounds ? Number(offPounds[1]) : null,
    start: hit ? hit.index : -1,
    end: hit ? hit.index + hit[0].length : -1,
    from: from ? `From ${pounds(from[1])}` : null,
    wasNow: wasNow ? `Was ${pounds(wasNow[1])}, now ${pounds(wasNow[2])}` : null,
    saving: wasNow && Number(wasNow[1]) > Number(wasNow[2]) ? Number(wasNow[1]) - Number(wasNow[2]) : null,
    wasStart: wasNow ? wasNow.index : -1,
    wasEnd: wasNow ? wasNow.index + wasNow[0].length : -1,
    usual: !wasNow && usual ? Number(usual[1]) : null,
  }
}
const nowPrice = (base, f) => {
  const v = f.percent ? base * (1 - f.percent / 100) : f.off ? base - f.off : null
  return v && v > 0 ? Math.round(v * 100) / 100 : null
}

// The service an offer is on: the words after "off", up to the next day,
// time or punctuation, or the words just before the offer ("balayage, 20% off")
const STOP_WORDS = 'this|next|on|until|till|til|for|with|from|when|all week|today|tonight|tomorrow|at|before|by|only|usually|normally|was|now|during|every|and\\s+(?:post|make|share|use|turn|put|schedule)'
const STOP = `(?=\\s+(?:${STOP_WORDS})\\b|\\s+${DAY_ANY}(?![a-z-])|\\s*[.,!;:(]|$)`
const serviceAfter = (text, from) => {
  if (from < 0) return null
  const after = text.slice(from)
  // "15% off for returning clients" says who it's for
  const who = /^\s*(for\s+(?:new|returning|first[\s-]time|all|our|every|students?|nhs|key)\s*[a-z' -]{0,30}?)(?=[.,!;:(]|\s+(?:this|next|until|till|on|all week|today|tomorrow)\b|$)/i.exec(after)
  if (who) return typo(clean(who[1]).toLowerCase())
  const m = new RegExp(`^\\s*(?:on\\s+)?((?:all|any|every|your|our)?\\s*[a-z][a-z&'/ -]{1,40}?)${STOP}`, 'i').exec(after)
  const s = m && clean(m[1])
  if (!s || new RegExp(`^(?:${STOP_WORDS}|it|this|that|the)\\b`, 'i').test(s)) return null
  return typo(s.toLowerCase())
}
const serviceBefore = (text, start) => {
  if (start <= 0) return null
  const before = text.slice(0, start).split(/[.,;:!?(]/).pop()
  const s = clean(before)
    .replace(/^(?:(?:please|can you|could you)\s+)?(?:post|make|share|do|create|put up|run)?\s*(?:an?\s+|our\s+|the\s+)?(?:offer|deal|discount|promo(?:tion)?|sale)?\s*(?:on|for)?\s*/i, '')
    .replace(/\s+(?:is|are|at|now|for)$/i, '')
  return s && /^[a-z][a-z&'/ -]{1,40}$/i.test(s) && !/^(?:it|this|that|the|get|got|have|save)$/i.test(s) ? typo(s.toLowerCase()) : null
}
const serviceFor = (text) => {
  const m = new RegExp(`\\bfor\\s+(?:a|an|some|your)?\\s*([a-z][a-z&'/ -]{2,30}?)${STOP}`, 'i').exec(text)
  const s = m && clean(m[1])
  return s && !/^(?:you|everyone|anyone|them|free|now|me|us|someone|somebody|the day)$/i.test(s) ? cap(typo(s)) : null
}

// What an offer's dates say: "This week only", "Tuesday to Thursday"...
const offerDates = (text, now) => {
  const range = new RegExp(`\\b${RANGE}`, 'i').exec(text)
  if (range && dayIndex(range[1]) >= 0 && dayIndex(range[2]) >= 0) return `${dayName(range[1])} to ${dayName(range[2])}`
  const week = /\b(this|next)\s+(week(?:end)?|month)\b|\ball\s+(week|month)\b/i.exec(text)
  if (week) {
    if (/^all/i.test(week[0])) return `All ${week[3].toLowerCase()}`
    const what = week[2].toLowerCase()
    return week[1].toLowerCase() === 'this' ? `This ${what} only` : `Next ${what}`
  }
  const until = new RegExp(`\\b(?:until|till|til|ends?|ending)\\s+(?:the\\s+)?(${DAY_ANY}(?![a-z-])|tomorrow|tonight|end of (?:the month|${MONTH_NAMES})|christmas|new year|\\d{1,2}(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH_NAMES}\\b|${MONTH_NAMES}\\s+\\d{1,2}(?:st|nd|rd|th)?)`, 'i').exec(text)
  if (until) {
    const w = until[1].toLowerCase()
    if (new RegExp(`^${DAY_ANY}$`, 'i').test(w)) return `Until ${dayName(w)}`
    if (/^christmas|^new year/.test(w)) return `Until ${w.replace(/\b[a-z]/g, c => c.toUpperCase())}`
    const endOf = new RegExp(`^end of ${MONTH_NAMES}`, 'i').exec(w)
    if (endOf) return `Until the end of ${moment().month(MONTHS[endOf[1].slice(0, 3)]).format('MMMM')}`
    if (/\d/.test(w)) { const d = findDays(w, now)[0]; if (d) return `Until ${d.date.format('D MMMM')}` }
    return /^end/.test(w) ? `Until the ${w}` : `Until ${w}`
  }
  const days = findDays(text, now)
  // "on Tuesdays" is every Tuesday
  const names = Array.from(new Set(days.map(d => (/today|tonight/i.test(d.phrase) ? 'Today' : /tomorrow|tmrw|tomoz/i.test(d.phrase) ? 'Tomorrow' : /\d/.test(d.phrase) ? d.date.format('D MMMM') : `${d.date.format('dddd')}${/days\b/i.test(d.phrase) ? 's' : ''}`))))
  if (names.length === 1) return `${names[0]} only`
  if (names.length > 1) return andList(names)
  return null
}
// A line about dates, lower case unless it starts with a name ("until Friday",
// "Tuesday to Thursday")
const saidDates = (s) => (new RegExp(`^(?:${DAY_ANY}|${MONTH_NAMES}|christmas|new year|\\d)`, 'i').test(s) ? s : lower(s))

// ─── The salon's menu ───
const norm = (s) => String(s || '').toLowerCase().replace(/colou?r/g, 'colour').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim()
const FILLER = /^(?:and|or|the|a|an|any|all|every|our|your|my|with|of|for|on|in|to|off|full|new|first|next|clients?|services?|treatments?|appointments?|sessions?)$/
const stem = (w) => (w.length > 3 ? w.replace(/ies$/, 'y').replace(/(sh|ch|x|ss)es$/, '$1').replace(/([^s])s$/, '$1') : w)
const tokensOf = (s) => norm(s).split(' ').filter(w => w && !FILLER.test(w)).map(stem)
// The one service on the menu a phrase means ("balayage" is Balayage, not
// Balayage and toner; "cut and finish" is Ladies cut and finish), or null
// when it could be more than one ("cuts": ladies or gents?)
export const matchService = (services, said) => {
  const want = tokensOf(said)
  if (!want.length || !services || !services.length) return null
  const hits = services.filter(s => s && s.name).map(s => ({ s, t: tokensOf(s.name) })).filter(x => x.t.length && want.every(w => x.t.includes(w)))
  const exact = hits.filter(x => x.t.length === want.length)
  if (exact.length) return exact.length === 1 ? exact[0].s : null
  // One word ("colour") is a kind of service, not one of them
  return hits.length === 1 && want.length >= 2 ? hits[0].s : null
}
const durationLabel = (d) => {
  const mins = parseInt(String(d || ''), 10)
  if (!mins) return ''
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const r = mins % 60
  return `${h} hr${h > 1 ? 's' : ''}${r ? ` ${r} min` : ''}`
}
const menuItems = (services, words) => {
  let list = (services || []).filter(s => s && s.name && Number(s.price) > 0)
  if (words && words.length) {
    const hit = list.filter((s) => { const t = tokensOf(`${s.name} ${s.category || ''}`); return words.some(w => t.includes(w)) })
    if (hit.length) list = hit
  }
  return list.slice(0, 8).map(s => ({ name: s.name, price: pounds(s.price), detail: durationLabel(s.duration) }))
}

// ─── What kind of post ───
const SERVICES = /\b(skin fades?|beard trims?|hot towel shaves?|lash lifts?|brow lamination|spray tans?|colou?r corrections?|blow[\s-]?dr(?:y|ies)|haircuts?|cuts?|trims?|colou?rs?|balayage|highlights|lowlights|foils|toners?|gloss(?:es)?|bridal|updos?|perms?|keratin|extensions|fades?|tapers?|beards?|shaves?|nails|manicures?|pedicures?|gel nails|biab|acrylics|lashes|brows|waxing|facials?|massages?|tints?)\b/i

// Checked in this order ("one free slot" before "free slots")
const JOB_NAMES = [
  ['oneSlot', /one\s+(?:free\s+)?slot|single\s+slot|cancell?ation/],
  ['freeSlots', /free\s+slots?|free\s+spaces?|availability|slots/],
  ['beforeAfter', /before\s*(?:and|&|\/)\s*after|transformation/],
  ['giftVouchers', /gift\s*vouchers?|vouchers?|gift\s*cards?/],
  ['newService', /new\s+(?:service|treatment)s?|now\s+offering|launch(?:ing)?/],
  ['priceList', /price\s*list|prices|menu/],
  ['review', /review|testimonial/],
  ['team', /meet\s+the\s+team|team|introduction|intro/],
  ['tips', /tips?|advice/],
  ['question', /question|faq/],
  ['offer', /offer|deal|discount|sale|promo(?:tion)?/],
  ['freshWork', /fresh\s+work|our\s+work|work\s+post|photo\s+post/],
]
// "make it a gift voucher post", "turn this into an offer", "change it to a tip"
const explicitJob = (text) => {
  const tail = '(?:\\s+post)?(?=[.,!]|$|\\s+(?:and|with|for|about|instead|please|saying|that)\\b)'
  const m = new RegExp(`\\b(?:make|turn|change|switch|convert)\\s+(?:it|this|the post)\\s+(?:in)?to\\s+(?:a|an)?\\s*([a-z &/-]{3,30}?)${tail}|\\bmake\\s+(?:it|this)\\s+(?:a|an)\\s+([a-z &/-]{3,30}?)${tail}`, 'i').exec(text)
  if (!m) return null
  const said = (m[1] || m[2] || '').toLowerCase()
  const hit = JOB_NAMES.find(([, re]) => re.test(said))
  return hit ? hit[0] : null
}
const NOT_A_NAME = new RegExp(`^(?:our|the|a|an|my|your|new|us|we|everyone|everybody|all|back|you|them|clients?|customers?|friends|guests|spring|summer|autumn|winter|christmas|easter|in|to|home|aboard|team|salon|shop|studio|today|tomorrow|tonight|week|weekend|month|stylist|barber|colou?rist|therapist|apprentice|starter|${DAY_FULL}|${MONTH_NAMES})$`, 'i')
const ROLE_WORDS = '(?:stylist|hairstylist|hairdresser|barber|colou?rist|specialist|therapist|nail tech(?:nician)?|technician|artist|manager|receptionist|educator|beautician|aesthetician|director)'
const INSTRUCTION = /^(?:post|share|make|use|put|schedule|remind|add|change|turn|set|give|show|hide|remove|take)\b/i

// ─── Colours, size and photos, said outright ───
const COLOUR_ALT = Object.keys(COLOUR_WORDS).sort((a, b) => b.length - a.length).join('|')
const SHADE = '(?:(?:dark|deep|light|pale|soft|bright)\\s+)?'
const COLOUR_RE = `(#[0-9a-f]{6}|${SHADE}(?:${COLOUR_ALT}))`
const ROLE_RE = '(background|backdrop|bg|words|text|writing|lettering|headline|heading|accent|highlight)'
const colourHex = (said) => {
  const s = String(said || '').toLowerCase().trim()
  if (/^#[0-9a-f]{6}$/.test(s)) return normHex(s)
  const m = /^(?:(dark|deep|light|pale|soft|bright)\s+)?([a-z]+)$/.exec(s)
  if (!m || !COLOUR_WORDS[m[2]]) return null
  const base = COLOUR_WORDS[m[2]]
  if (m[1] === 'dark' || m[1] === 'deep') return normHex(mix(base, '#111111', 0.35))
  if (m[1] === 'light' || m[1] === 'pale' || m[1] === 'soft') return normHex(mix(base, '#FFFFFF', 0.45))
  return base
}
const roleOf = (word) => (/^(?:background|backdrop|bg)$/i.test(word) ? 'ground' : /^(?:accent|highlight)$/i.test(word) ? 'accent' : 'words')
// Which colour each of those is, per design family
const ROLE_KEY = {
  ground: { editorial: 'paper', quiet: 'paper', poster: 'accent', layouts: 'paper', layoutsBold: 'accent', layoutsDark: 'ink' },
  words: { editorial: 'ink', quiet: 'ink', poster: 'paper', layouts: 'ink', layoutsBold: 'on', layoutsDark: 'paper' },
  accent: { editorial: 'accent', quiet: 'pop', poster: 'paper', layouts: 'accent', layoutsBold: 'paper', layoutsDark: 'accent' },
}
const ROLE_SAID = { ground: 'Background', words: 'Words', accent: 'Accent' }
const FILTER_RE = {
  mono: 'black and white|black & white|b&w|b\\/w|mono(?:chrome)?|gr[ae]y ?scale',
  warm: 'warm(?:er)?',
  cool: 'cool(?:er)?',
  bright: 'bright(?:er|en)?|lighter',
  none: 'no filter|without (?:a|the) filter|original colou?rs?|natural colou?rs?',
}
const FILTER_SAID = { mono: 'Photo in black and white', warm: 'Warmer photo', cool: 'Cooler photo', bright: 'Brighter photo', none: 'Photo back to its own colours' }
const PHOTO_WORD = '(?:photos?|pictures?|images?|pics?)'

// Words aimed at one place on the post: "headline: ...", "last line to ..."
const TARGETS = [
  ['cta', 'last line|booking line|cta|call to action|bottom line|button'],
  ['small', 'small heading|eyebrow|top line|label|kicker'],
  ['detail', 'small line|detail|subtitle|sub-?heading|strapline|tagline'],
  ['note', 'note|small print'],
  ['main', 'headline|title|heading|main line|big words|wording'],
]
const CONNECT = '(?:to say|to read|should say|should read|to|:|=|as|is|says|reads|say|read)'
const UNTIL_END = '(?=[.!?"]|,?\\s+and\\s+(?:post|make|use|share|put|schedule|add|change|turn)\\b|,\\s*(?:post|make|use|share|put|schedule|add|change|turn)\\b|$)'

// ─── Reading the request ───
// Everything asked for, before anything changes
export const parseRequest = (input, { now = new Date() } = {}) => {
  const text = clean(String(input || '').replace(/[‘’]/g, "'").replace(/[“”]/g, '"'))
  const req = {
    text, job: null, jobExplicit: null, fields: {}, names: [], reviewPick: null,
    schedule: null, when: null, format: null, tune: {}, caption: {}, look: null, lookText: '',
    undo: false, shareNow: false, fromDiary: false, fromMenu: false, known: false,
  }
  if (!text) { req.look = understand(''); return req }
  if (/^(?:undo|undo that|go back|put it back|change it back|revert)\b/i.test(text)) { req.undo = true; req.known = true; req.look = understand(''); return req }
  const used = [] // phrases already read, kept out of the look
  const f = req.fields

  // When to post it: "post it Friday at 6pm", "share this tomorrow morning",
  // "schedule it for Monday", "remind me tonight", "post it now"
  const verb = '(?:post|share|publish|put)(?:\\s+(?:it|this|that|something|one|a post))?(?:\\s+(?:up|out|live))?|schedule(?:\\s+(?:it|this|that|something|one|a post))?|go(?:es)?\\s+(?:out|live|up)|remind\\s+me(?:\\s+to\\s+(?:post|share)(?:\\s+(?:it|this))?)?'
  const start = `(?:(?:on|for|this|next|at|by)\\s+)?(?:today|tonight|tomorrow|tmrw|${DAY_ANY}(?![a-z-])|\\d{1,2}(?:[:.]\\d{2})?\\s?(?:am|pm)\\b|\\d{1,2}(?:st|nd|rd|th)\\b|${MONTH_NAMES}\\s+\\d|noon\\b|midday\\b|lunchtime\\b|(?:this|in the)\\s+(?:morning|afternoon|evening)\\b|first thing\\b)|at\\s+\\d{1,2}(?:[:.]\\d{2})?\\b|now\\b|right now\\b|straight away\\b`
  const sched = new RegExp(`\\b(${verb})\\s+((?:${start})(?:[^,.;!?]|\\.(?=\\d))*?)(?=\\s+(?:and|with|about|saying|but)\\s|[,;!?]|\\.(?!\\d)|$)`, 'i').exec(text)
  let rest = text
  if (sched) {
    if (/^(?:right\s+)?now\b|^straight away\b/i.test(sched[2])) req.shareNow = true
    else {
      const at = whenFrom(sched[2], now, { loose: true })
      if (at) req.schedule = { at, phrase: clean(sched[0]) }
    }
    if (req.shareNow || req.schedule) {
      used.push(sched[0])
      rest = tidy(text.slice(0, sched.index) + text.slice(sched.index + sched[0].length))
    }
  }

  // The format
  if (/\b(?:story|stories|reel|tiktok|vertical|9:16)\b/i.test(text)) req.format = 'story'
  else if (/\b(?:feed post|grid post|square post|portrait post|4:5)\b|\bmake it a post\b|\bnot a story\b/i.test(text)) req.format = 'post'
  if (req.format) used.push(...(text.match(/\b(?:story|stories|reel|tiktok|vertical|9:16|feed post|grid post|square post|portrait post|4:5)\b/gi) || []))

  // The salon's name, logo and last line
  const logo = /\b(?:use|add|show|put|with|include)\b[^.,]{0,20}\blogo\b|\blogo\s+on\b/i.exec(text)
  const noLogo = /\b(?:remove|hide|no|without|take off|drop)\b[^.,]{0,20}\blogo\b/i.exec(text)
  if (noLogo) { req.tune.brand = 'name'; used.push(noLogo[0]) } else if (logo) { req.tune.brand = 'logo'; used.push(logo[0]) }
  const noName = /\b(?:remove|hide|no|without|take off|drop)\b[^.,]{0,20}\b(?:salon name|our name|the name|my name|name)\b/i.exec(text)
  if (noName && !/\b(?:their|her|his)\s+name\b/i.test(noName[0])) { req.tune.brand = 'none'; used.push(noName[0]) }
  const noCta = /\b(?:remove|hide|no|without|take off|drop)\b[^.,]{0,20}\b(?:booking line|last line|cta|call to action|link in bio)\b/i.exec(text)
  const yesCta = /\b(?:show|add|put back|bring back)\b[^.,]{0,20}\b(?:booking line|last line)\b/i.exec(text)
  if (noCta) { req.tune.hideCta = true; used.push(noCta[0]) } else if (yesCta) { req.tune.hideCta = false; used.push(yesCta[0]) }

  // Colours for one part of the post: "pink background", "words in white"
  const colours = []
  const spans = []
  let m
  const roleFirst = new RegExp(`\\b${ROLE_RE}\\s+(?:colou?r\\s+)?(?:to|in|=|:|as|is|into|should be|be)?\\s*(?:an?\\s+)?${COLOUR_RE}\\b`, 'gi')
  while ((m = roleFirst.exec(text))) {
    const hex = colourHex(m[2])
    if (hex) { colours.push({ role: roleOf(m[1]), hex, said: m[2].toLowerCase() }); used.push(m[0]); spans.push([m.index, m.index + m[0].length]) }
  }
  const colourFirst = new RegExp(`(?:^|\\s)${COLOUR_RE}\\s+${ROLE_RE}\\b`, 'gi')
  while ((m = colourFirst.exec(text))) {
    const a = m.index
    const b = m.index + m[0].length
    const hex = colourHex(m[1])
    if (hex && !spans.some(([x, y]) => a < y && b > x)) { colours.push({ role: roleOf(m[2]), hex, said: m[1].toLowerCase() }); used.push(m[0]) }
  }
  if (colours.length) req.tune.colours = colours

  // Bigger or smaller words (not the photo or logo)
  const aboutPhoto = new RegExp(`\\b(?:${PHOTO_WORD}|logo)\\b[^.,]{0,25}\\b(?:bigger|larger|smaller)\\b|\\b(?:bigger|larger|smaller)\\b[^.,]{0,25}\\b(?:${PHOTO_WORD}|logo)\\b`, 'i').test(text)
  const up = /\b(?:(much|a lot|way|loads)\s+)?(?:bigger|larger|huge)\b(?:\s+(?:text|words|writing|headline|font))?|\bincrease\s+the\s+(?:text|font|size)\b/i.exec(text)
  const down = /\b(?:(much|a lot|way|loads)\s+)?(?:smaller|tiny|less big)\b(?:\s+(?:text|words|writing|headline|font))?|\breduce\s+the\s+(?:text|font|size)\b/i.exec(text)
  if (!aboutPhoto && (up || down)) { const hit = up || down; req.tune.size = (up ? 1 : -1) * (hit[1] ? 2 : 1); used.push(hit[0]) }

  // A photo filter: "make the photo black and white", "warmer photo"
  Object.keys(FILTER_RE).some((k) => {
    const r = new RegExp(`\\b${PHOTO_WORD}\\b[^.,;]{0,30}?\\b(?:${FILTER_RE[k]})\\b|\\b(?:${FILTER_RE[k]})\\b[^.,;]{0,12}?\\b${PHOTO_WORD}\\b`, 'i').exec(text)
    if (r) { req.tune.filter = k; used.push(r[0]) }
    return !!r
  })

  // The caption: a voice, hashtags added or taken off
  const captionSaid = clauses(text).filter(s => /\bcaption\b|\bhashtags?\b/i.test(s))
  const cs = captionSaid.join(' ')
  if (/\bcaption\b/i.test(cs)) {
    if (/\b(?:fun|funny|funnier|playful|cheeky|witty|jokey)\b/i.test(cs)) req.caption.voice = 'fun'
    else if (/\b(?:short|shorter|brief|snappy|punchier)\b/i.test(cs)) req.caption.voice = 'short'
    else if (/\b(?:warm|warmer|friendl\w*|longer|detailed|kinder)\b/i.test(cs)) req.caption.voice = 'warm'
    else if (/\b(?:rewrite|write it again|new caption|another caption|different caption)\b/i.test(cs)) req.caption.rewrite = true
  }
  const tags = (text.match(/#[A-Za-z][A-Za-z0-9_]*/g) || []).filter(t => !(/^#[0-9a-f]{6}$/i.test(t) && /\d/.test(t)))
  if (tags.length) req.caption.add = Array.from(new Set(tags))
  if (/\b(?:no|remove|without|drop|take off)\s+(?:the\s+|all\s+(?:the\s+)?)?hashtags\b/i.test(text)) req.caption.clear = true
  used.push(...captionSaid, ...tags)

  // Words for a place on the post, quoted or after "to" / ":"
  const words = {}
  const quotedUsed = new Set()
  const aimed = []
  TARGETS.forEach(([target, names]) => {
    const r = new RegExp(`\\b(?:the\\s+)?(?:${names})\\s*(?:(${CONNECT})\\s*)?(?:"([^"]{1,120})"|'([^']{2,120})'(?=\\s|[.,!?]|$)|([^".!?]{2,90}?)${UNTIL_END})`, 'i').exec(rest)
    if (!r || words[target]) return
    // "heading" inside "small heading" or "sub-heading" isn't the headline
    if (target === 'main' && /\b(?:small|sub|top)[\s-]?(?:the\s+)?$/i.test(rest.slice(0, r.index + (/^the\s/i.test(r[0]) ? 4 : 0)))) return
    const value = r[2] || r[3] || (r[1] ? r[4] : null)
    if (!value || !clean(value)) return
    // "make the headline pink" is a colour, "the headline bigger" a size
    if (!r[2] && !r[3] && (colourHex(value) || /^(?:bigger|smaller|larger|size|font|colou?r|bold|up|down|shorter|longer|italic)\b/i.test(clean(value)))) return
    words[target] = typo(value)
    if (r[2] || r[3]) quotedUsed.add(clean(r[2] || r[3]))
    used.push(r[0])
    aimed.push(r[0])
  })
  // A quote on its own is the main words (or a review, on a review post)
  const quoted = /"([^"]{2,160})"/.exec(rest) || /(?:^|\s)'([^']{3,160})'(?=\s|[.,!?]|$)/.exec(rest)
  if (quoted && !quotedUsed.has(clean(quoted[1]))) { f.quote = typo(quoted[1]); used.push(quoted[0]); aimed.push(quoted[0]) }
  if (Object.keys(words).length) f.words = words

  // The rest is about the post itself: its offer, days, times, people. Words
  // meant for a headline or the caption aren't ("open late on Thursdays")
  let body = rest
  aimed.concat(captionSaid, tags).forEach((s) => { if (s) body = body.replace(s, ' ') })
  body = tidy(body)
  req.body = body

  // Money, dates and what the offer is on
  const cash = findMoney(body)
  if (cash.discount) { f.discount = cash.discount; f.percent = cash.percent; f.off = cash.off; used.push(body.slice(cash.start, cash.end)) }
  if (cash.from) f.from = cash.from
  if (cash.wasNow) { f.wasNow = cash.wasNow; f.saving = cash.saving }
  if (cash.usual) f.usual = cash.usual
  if (cash.discount || cash.wasNow) {
    const svc = cash.discount ? serviceAfter(body, cash.end) || serviceBefore(body, cash.start) : serviceAfter(body, cash.wasEnd) || serviceBefore(body, cash.wasStart)
    if (svc) { f.service = svc; used.push(svc) }
  }
  // "Welcome back offer: ..." names the offer
  const named = /^([a-z][a-z' -]{2,30}?\s+(?:offer|deal|special|sale))\s*[:\-–]/i.exec(body)
  if (named && !/^(?:an?|the|our|my|new)\s+(?:offer|deal|special|sale)$/i.test(clean(named[1])) && !/^(?:post|make|share|create|do|turn|change|switch|convert|put|add)\b/i.test(named[1]) && !/\b(?:it|this|into)\b/i.test(named[1])) f.offerName = cap(typo(named[1]))
  const dates = offerDates(body, now)
  if (dates) f.dates = dates
  const days = findDays(body, now)
  if (days.length) f.date = isoDay(days[0].date.toDate())
  const times = findTimes(body)
  if (times.length) f.times = times.map(t => t.label)
  if (/\bmore\s+(?:times|slots|spaces)\b/i.test(text)) f.more = true
  if (/\b(?:from|use|with|fill it from|check)\s+(?:my|our|the)\s+diary\b|\b(?:my|our)\s+(?:free\s+(?:times|slots|spaces|appointments)|gaps)\b/i.test(text)) req.fromDiary = true
  if (/\b(?:from|use|with)\s+(?:my|our|the)\s+(?:menu|price list|services|prices)\b|\b(?:our|my|real)\s+prices\b/i.test(text)) req.fromMenu = true

  // People: "meet Jess, our new colour specialist. She's great with blondes"
  const intro = new RegExp(`\\b(?:meet|introduc(?:e|ing)|welcome|say (?:hello|hi) to|give a (?:big |warm )?welcome to)\\s+(?:our\\s+)?(?:new\\s+)?(?:(?:[a-z]+\\s+)?${ROLE_WORDS}\\s*,?\\s+)?([A-Za-z][a-z'-]*[a-z])\\b`, 'i').exec(body)
  const joined = /\b([A-Z][a-z'-]+)\s+(?:has|have)\s+joined\b|\b([A-Z][a-z'-]+)\s+(?:joins|is joining|is starting|starts)\b/.exec(body)
  const nameWord = intro ? intro[1] : joined ? (joined[1] || joined[2]) : null
  if (nameWord && !NOT_A_NAME.test(nameWord)) {
    f.name = (/[A-Z]/.test(nameWord) ? nameWord : cap(nameWord)).replace(/'s$/i, '')
    const role = new RegExp(`\\b(?:our|as our|as a|as an|a|the)\\s+(?:new\\s+)?((?:senior|junior|lead|head|trainee|apprentice|master|top|creative)?\\s*(?:[a-z]+\\s+)?${ROLE_WORDS})\\b`, 'i').exec(body)
    if (role) f.role = cap(clean(role[1]).toLowerCase())
    // What's said about them: the sentences after, or "who's brilliant with blondes"
    const all = sentences(body)
    const later = all.slice(1).filter(s => !INSTRUCTION.test(s) && !/\bcaption\b/i.test(s) && !/"/.test(s))
    const who = /\b(?:who|she|he|they)(?:'s|\s+(is|has|joins|brings|loves|specialises in|specializes in|does|knows))\s+([^.!?]+)/i.exec(all[0] || '')
    let line = later.length ? later.join(' ') : who ? `${who[1] && !/^is$/i.test(who[1]) ? `${who[1]} ` : ''}${who[2]}` : ''
    // Not when it only says their role again
    if (line && f.role && norm(line).replace(/^(?:our|a|the)\s+(?:new\s+)?/, '').startsWith(norm(f.role))) line = ''
    if (clean(line)) f.line = sentence(typo(line))
    used.push(nameWord)
    if (f.role) used.push(f.role)
    if (later.length) used.push(...later)
    else if (who) used.push(who[0])
  }
  const reviewBy = /\b([A-Za-z][a-z-]+)'s\s+(?:(?:latest|new|lovely|great|five star|5 star)\s+)?review\b|\breview\s+(?:from|by)\s+([A-Za-z][a-z'-]+)/i.exec(body)
  if (reviewBy && !NOT_A_NAME.test(reviewBy[1] || reviewBy[2])) req.names.push(cap(String(reviewBy[1] || reviewBy[2]).toLowerCase()))
  const pick = /\b(?:latest|newest|most recent|recent|last|best|top)\s+(?:(?:5|five)[\s-]*star\s+)?review\b/i.exec(body)
  if (pick) req.reviewPick = /\b(?:best|top)\b/i.test(pick[0]) ? 'best' : 'latest'
  const by = /\b(?:by|with)\s+([A-Za-z][a-z'-]+)\b/.exec(body)
  if (by && !NOT_A_NAME.test(by[1]) && !SERVICES.test(by[1])) f.byRaw = { name: by[1], capital: /^[A-Z]/.test(by[1]) }

  // A tip, a question, a price list
  const tip = /\btips?\s*(?:about|on|for)?\s*([^:.!?]{0,60}?)\s*(?::|\s[-–]\s)\s*(.+)$/i.exec(body)
  if (tip && clean(tip[2]).length > 3) {
    f.tipHead = typo(tip[1])
    f.tipBody = typo(sentences(tip[2]).filter(s => !INSTRUCTION.test(s)).join(' ') || tip[2])
    used.push(tip[0])
  }
  const asksBailey = /\b(?:can|could|would|will)\s+(?:you|it)\b|\b(?:make|change|turn)\s+(?:it|this)\b|\bit\s+be\b/i.test(text)
  const flagged = /\b(?:question|faq)\b|^q\s*[:\-–]/i.test(text)
  const opens = /^(?:how|what|why|when|which|should|do|does|is|are|can|will|who|where)\b/i.test(text)
  if ((flagged || opens) && !asksBailey && /\?/.test(body)) {
    const qa = /([^.!?:]{4,140}\?)\s*(.*)$/.exec(body)
    if (qa) {
      f.question = cap(typo(qa[1].replace(/^\s*(?:a\s+)?(?:question|faq)\s*(?:about|on)?\s*[:\-–]?\s*/i, '')))
      const answer = sentences(qa[2].replace(/^\s*a(?:nswer)?\s*[:\-–]\s*/i, '')).filter(s => !INSTRUCTION.test(s)).join(' ')
      if (clean(answer)) f.answer = sentence(typo(answer))
      used.push(qa[0])
    }
  }
  const items = []
  const pi = /([A-Za-z][A-Za-z &'/-]{1,30}?)\s*(?:-|–|:|at|for)?\s*£\s?(\d+(?:\.\d{2})?)(?!\s?off)/g
  let p
  while ((p = pi.exec(body))) {
    const name = clean(p[1]).replace(/^(?:and|price list|prices|menu|our)\s*:?\s*/i, '').replace(/\s+(?:from|was|now|at|for|only|just)$/i, '').trim()
    if (name && !/^(?:from|was|now|or|and|vouchers?|gift|gift vouchers?|off|usually|normally|save|rrp)$/i.test(name)) items.push({ name: cap(typo(name)), price: pounds(p[2]), detail: '' })
  }
  if (items.length >= 2) { f.items = items; used.push(...items.map(i => i.name)) }
  const menuFor = /\b(?:price\s*list|prices|menu)\s+(?:for|of)\s+(?:our\s+|your\s+|the\s+)?([a-z &'-]{3,40}?)(?=[.,!]|$|\s+(?:and|with|post|share|this|next|on)\b)|\b(?:our\s+|the\s+)?([a-z]{3,20}(?:\s+[a-z]{3,20})?)\s+(?:price\s*list|prices|menu)\b/i.exec(body)
  if (menuFor) {
    const want = tokensOf(menuFor[1] || menuFor[2]).filter(w => !/^(?:post|share|make|real|price|list|whole|latest|current|updated|do|create|put)$/.test(w))
    if (want.length) f.menuWords = want
  }

  // When a day and time in the words (not a content phrase) mean "post it then"
  let whenText = body
  ;[f.line, f.tipBody, f.question, f.answer, f.quote].concat(Object.values(words)).forEach((s) => { if (s) whenText = whenText.replace(new RegExp(escapeRe(clean(s).replace(/’/g, "'")), 'ig'), ' ') })
  req.when = whenFrom(whenText, now)

  // What kind of post this is
  req.jobExplicit = explicitJob(text)
  const implied = () => {
    // "introduce our new balayage service" is a new service, not someone new
    if (/\bnew\s+(?:[a-z'-]+\s+){0,3}(?:service|treatment)s?\b/i.test(text)) return 'newService'
    if (f.name && /\b(?:meet|introduc\w*|welcome|say (?:hello|hi) to|joined|joins|joining|starts|starting)\b/i.test(text)) return 'team'
    if (/\bmeet the team\b|\bteam (?:post|intro)\b/i.test(text)) return 'team'
    if (/\bbefore\s*(?:and|&|\/)\s*after\b|\btransformation\b/i.test(text)) return 'beforeAfter'
    if (req.names.length || req.reviewPick || /\b(?:share|post)\b[^.]*\breview\b/i.test(text)) return 'review'
    if (/\bvouchers?\b|\bgift\s*cards?\b/i.test(text)) return 'giftVouchers'
    if (/\bnew\s+(?:service|treatment)s?\b|\bnow\s+(?:offering|doing)\b|\blaunch(?:ing|es)?\b|\bnew\s+(?:on|to)\s+(?:the|our)\s+menu\b/i.test(text)) return 'newService'
    if (f.discount || f.wasNow || /\b(?:offer|deal|discount|promo(?:tion)?)\b/i.test(text)) return 'offer'
    if (/\b(?:one|a|1)\s+(?:free\s+)?(?:slot|space|appointment|opening)\b|\bcancell?ation\b|\bspace just opened\b|\bfree\s+(?:slot|space|appointment)\b/i.test(text)) return 'oneSlot'
    if (/\bfree\s+(?:slots|spaces|appointments|times)\b/i.test(text)) return 'freeSlots'
    if (/\b(?:slots|spaces|openings|availability|gaps)\b|\blast[\s-]minute\b/i.test(text)) return (f.times || []).length === 1 ? 'oneSlot' : 'freeSlots'
    if (f.items || f.menuWords || /\bprice\s*list\b|\b(?:our|my)\s+(?:[a-z]+\s+)?(?:prices|menu)\b/i.test(text)) return 'priceList'
    if (f.tipBody || /\b(?:a|our|one)\s+tips?\b|\btips?\s+(?:about|on|for)\b/i.test(text)) return 'tips'
    if (f.question) return 'question'
    if (/\b(?:fresh work|our work|show off|latest work|recent work|new look)\b/i.test(text)) return 'freshWork'
    return null
  }
  req.job = req.jobExplicit || implied()

  // What's left says how it should look (the remix reads it)
  let look = ` ${text} `
  const drop = (s) => { const t = clean(s).replace(/’/g, "'"); if (t.length > 1) look = look.replace(new RegExp(escapeRe(t), 'ig'), ' ') }
  used.forEach(drop)
  ;[f.service, f.role, f.line, f.dates, f.tipHead, f.tipBody, f.question, f.answer, f.name].forEach(drop)
  look = look.replace(/\b(?:it'?s|it is|we'?re|we are|been|diary(?:'s| is)?|a bit|very|really|so)\s+quiet\b/gi, ' ')
  req.lookText = tidy(look)
  req.look = understand(req.lookText)

  req.known = !!(req.shareNow || req.job || req.schedule || req.format || Object.keys(req.tune).length || Object.keys(req.caption).length
    || Object.keys(f).length || req.names.length || req.reviewPick || req.fromDiary || req.fromMenu || req.look.known)
  return req
}

// ─── Doing it ───
export const JOB_SAID = {
  freeSlots: 'a free slots post', oneSlot: 'a one free slot post', freshWork: 'a fresh work post', beforeAfter: 'a before and after',
  review: 'a review post', team: 'a meet the team post', newService: 'a new service post', priceList: 'a price list', tips: 'a tip', question: 'a question post',
  offer: 'an offer post', giftVouchers: 'a gift vouchers post',
}
// Where "the headline" and friends go on each kind of post
const MAIN_KEY = { priceList: 'title', offer: 'eyebrow', team: 'eyebrow', review: 'quote' }
const TARGET_KEY = {
  main: (type) => MAIN_KEY[type] || (POST_TYPES[type].fields.some(x => x.key === 'headline') ? 'headline' : null),
  cta: () => 'cta',
  small: (type) => ({ freeSlots: 'eyebrow', team: 'eyebrow', offer: 'eyebrow', giftVouchers: 'eyebrow', tips: 'label', newService: 'eyebrow', question: 'eyebrow' }[type] || null),
  detail: (type) => ({ freshWork: 'detail', beforeAfter: 'detail', oneSlot: 'line', newService: 'detail', tips: 'detail', question: 'detail' }[type] || null),
  note: (type) => (type === 'priceList' ? 'note' : null),
}
const TARGET_SAID = { main: 'headline', cta: 'last line', small: 'small heading', detail: 'small line', note: 'note' }
// A post Bailey starts fresh keeps none of the template's example facts (a
// price, a person, a time); they're emptied until the salon says
// What only the salon can know in each kind (postTypes.js)
export const FACTS = EXAMPLE_FACTS
const WHAT = {
  offer: { value: 'The offer, like 20% off or £10 off', service: 'What the offer is on', price: 'The full price, so clients know what they’ll pay', dates: 'When the offer runs' },
  giftVouchers: { from: 'The starting price' },
  oneSlot: { time: 'The time' },
  freeSlots: { times: 'The times' },
  team: { name: 'Their name', role: 'Their role' },
  priceList: { items: 'Your services and prices' },
  review: { quote: 'The review, word for word', name: 'Their first name' },
  newService: { headline: 'The new service’s name', body: 'A line about it' },
}
const PHOTO_NEEDS = {
  freshWork: [['main', 'A photo of your own work']],
  team: [['main', 'Their photo']],
  beforeAfter: [['before', 'The before photo'], ['after', 'The after photo']],
}
// Empties the template's example facts a new post (or a new kind of post)
// hasn't been given, and says what's still needed
export const emptyFacts = (type, { content, touched, photos = {}, given = new Set(), need }) => {
  (FACTS[type] || []).forEach((k) => {
    if (given.has(k)) return
    content[k] = Array.isArray(content[k]) ? [] : ''
    delete touched[k]
    const w = WHAT[type] && WHAT[type][k]
    if (w) need(w, k)
  })
  ;(PHOTO_NEEDS[type] || []).forEach(([k, text]) => {
    const src = photos[k] && photos[k].src
    if (!src) need(text, `photo:${k}`)
    else if (isStock(src)) need(`${text}. This one is a stock photo`, `photo:${k}`)
  })
}
// What a post still needs: the facts its kind has to say, and its own photos
export const missingFacts = (post) => {
  const out = []
  const need = (text, field) => { if (!out.some(n => n.text === text)) out.push({ text, field: field || null }) }
  const c = (post && post.content) || {}
  ;(FACTS[post.type] || []).forEach((k) => {
    const v = c[k]
    if (v === '' || v == null || (Array.isArray(v) && !v.length)) { const w = WHAT[post.type] && WHAT[post.type][k]; if (w) need(w, k) }
  })
  ;(PHOTO_NEEDS[post.type] || []).forEach(([k, text]) => {
    const src = post.photos && post.photos[k] && post.photos[k].src
    if (!src) need(text, `photo:${k}`)
    else if (isStock(src)) need(`${text}. This one is a stock photo`, `photo:${k}`)
  })
  return out
}
// Days that are part of the post itself, not when to post it
const DAYS_ARE_CONTENT = ['freeSlots', 'oneSlot', 'offer']
const whenSaid = (m, now) => moment(m).calendar(now, { sameDay: '[today at] h:mma', nextDay: '[tomorrow at] h:mma', nextWeek: 'dddd [at] h:mma', sameElse: 'ddd D MMM [at] h:mma' }).replace(':00', '')
const daySaid = (iso, now) => moment(iso).calendar(now, { sameDay: '[today]', nextDay: '[tomorrow]', nextWeek: 'dddd', sameElse: 'ddd D MMM', lastDay: '[yesterday]', lastWeek: '[last] dddd' })
const kindReview = (r) => r && Number(r.rating) >= 4 && String(r.text || '').length >= 20
// A tip as a headline, and the rest as the words under it
const splitTip = (tip) => {
  const t = sentence(tip)
  if (t.length <= 70) return { headline: t, body: '' }
  const first = sentences(t)[0]
  if (first && first.length <= 70 && first.length < t.length) return { headline: first, body: sentence(t.slice(first.length)) }
  const cut = /^(.{12,70}?)(?:,|;|\s[-–])\s+(.+)$/.exec(t)
  if (cut) return { headline: sentence(cut[1]), body: sentence(cut[2]) }
  return { headline: 'Worth knowing.', body: t }
}

// The request applied to a post. ctx: { now, kit, trade, team, reviews,
// services, vouchers, freeDay: { date, times, count }, diaryRead,
// captionFor(post, voice), fresh }. Returns { post, changes, needs: [{ text,
// field }], notes, given, look, lookText }: what changed, what's still
// needed (and where it goes), anything worth knowing, the fields the salon
// gave, and the look words for the remix.
export const applyRequest = (post, req, ctx = {}) => {
  const now = ctx.now || new Date()
  const changes = []
  const needs = []
  const notes = []
  const need = (text, field) => { if (!needs.some(n => n.text === text)) needs.push({ text, field: field || null }) }
  const job = req.job && POST_TYPES[req.job] ? req.job : null
  const switched = !!job && job !== post.type
  let next = post
  if (switched) { next = changeType(post, job); changes.push(`Made it ${JOB_SAID[job]}`) }
  const fresh = !!ctx.fresh || switched
  const type = next.type
  const def = POST_TYPES[type]
  const has = (k) => def.fields.some(x => x.key === k)
  const label = (k) => { const fd = def.fields.find(x => x.key === k); return fd ? fd.label : cap(k) }
  const content = { ...next.content }
  const touched = { ...(next.touched || {}) }
  let photos = next.photos || {}
  let source = next.source
  const given = new Set()
  const put = (k, v) => { if (!has(k) || v === undefined || v === null) return false; content[k] = v; touched[k] = true; given.add(k); return true }
  const f = req.fields || {}
  const lockedReview = type === 'review' && /^review:/.test(next.source || '')
  const team = ctx.team || []
  const member = (name) => team.find(x => x && x.name && firstName(x.name).toLowerCase() === String(name || '').toLowerCase())
  const byWho = f.byRaw && member(f.byRaw.name)
  const by = f.byRaw ? (byWho ? firstName(byWho.name) : f.byRaw.capital ? f.byRaw.name : null) : null

  // Words for a place on the post
  const words = { ...(f.words || {}) }
  if (f.quote && !words.main) words.main = f.quote
  Object.keys(words).forEach((target) => {
    const key = TARGET_KEY[target](type)
    if (key === 'quote' && lockedReview) { notes.push('A client’s review stays word for word, so I left it as it is.'); return }
    if (!key || !has(key)) { notes.push(`This kind of post has no ${TARGET_SAID[target]}.`); return }
    put(key, words[target])
    changes.push(`${label(key)}: “${plain(words[target])}”`)
  })

  if (type === 'offer') {
    const value = f.discount || (f.saving ? `${pounds(f.saving)} off` : null)
    if (value) { put('value', value); changes.push(`Offer: ${value}`) }
    if (f.service) { put('service', f.service); changes.push(`On: ${f.service}`) }
    if (f.offerName && !words.main && !words.small) { put('eyebrow', f.offerName); changes.push(`${label('eyebrow')}: ${f.offerName}`) }
    const onMenu = matchService(ctx.services, f.service || (!fresh && content.service))
    let price = f.wasNow || null
    let fromMenu = null
    if (!price && (f.percent || f.off)) {
      let base = f.usual || null
      if (!base && onMenu && Number(onMenu.price) > 0) { base = Number(onMenu.price); fromMenu = onMenu.name }
      const after = base ? nowPrice(base, f) : null
      if (after) price = `Was ${pounds(base)}, now ${pounds(after)}`
    }
    if (price) { put('price', price); changes.push(fromMenu ? `Price: ${lower(price)}, from ${fromMenu} on your menu` : `Price: ${lower(price)}`) } else if (f.discount && !fresh && content.price) need('Check the full price still matches the offer', 'price')
    if (f.dates) {
      put('dates', f.dates)
      changes.push(`When: ${saidDates(f.dates)}`)
      // The template's small heading ("This week only") would say otherwise
      if (!given.has('eyebrow') && !touched.eyebrow && content.eyebrow) { content.eyebrow = ''; touched.eyebrow = true }
    }
  }

  if (type === 'newService') {
    // "launching our new gloss refresh", "now offering beard detailing",
    // "new treatment: the reset facial"
    const said = /\b(?:new\s+(?:service|treatment)s?(?:\s*[:,]|\s+called|\s+is)?|launch(?:ing|es)?|now\s+(?:offering|doing)|introduc(?:e|ing))\s+(?:our\s+|the\s+|a\s+|an\s+)?(?:new\s+)?([a-z][a-z' &]{2,40}?)(?=\s*(?:[.,!:]|$)|\s+(?:from|for|at|this|next|on|in|with|service|treatment|and\s+(?:post|make|share))\b)/i.exec(req.body || req.text || '')
    const name = !words.main && said && !/^(?:service|treatment|post|one)$/i.test(said[1].trim()) ? cap(said[1].trim()) : null
    if (name) { put('headline', `${name}.`); changes.push(`${label('headline')}: “${name}”`) }
    if (f.from && !words.detail) { put('detail', f.from); changes.push(`${label('detail')}: ${f.from}`) }
  }

  if (type === 'giftVouchers') {
    let from = f.from || null
    let fromVouchers = false
    if (!from && (fresh || req.fromMenu) && ctx.vouchers && ctx.vouchers.length) {
      const min = Math.min(...ctx.vouchers.map(v => Number(v.fromPrice || v.value) || Infinity))
      if (Number.isFinite(min) && min > 0) { from = `From ${pounds(min)}`; fromVouchers = true }
    }
    if (from) { put('from', from); changes.push(fromVouchers ? `Price: ${lower(from)}, your lowest voucher price` : `Price: ${lower(from)}`) }
    if (fresh && ctx.vouchers && !ctx.vouchers.length) need('Gift vouchers on sale online, so the link works. Set them up in Gift cards first')
    const occasion = /\b(christmas|valentine'?s(?: day)?|mother'?s day|father'?s day|black friday|birthdays?|easter|anniversar(?:y|ies)|graduation)\b/i.exec(req.body || req.text)
    const small = f.discount ? `${f.discount}${f.dates ? `, ${saidDates(f.dates)}` : ''}` : occasion ? occasion[1].toLowerCase().replace(/'/g, '’').replace(/\b[a-z]/g, c => c.toUpperCase()) : null
    if (small && !words.small && !words.main) { put('eyebrow', cap(small)); changes.push(`${label('eyebrow')}: ${cap(small)}`) }
  }

  if (type === 'freeSlots' || type === 'oneSlot') {
    if (f.date) { put('date', f.date); changes.push(`Day: ${daySaid(f.date, now)}`) }
    const said = f.times || []
    if (type === 'freeSlots' && said.length) {
      put('times', said.slice(0, 3))
      if (said.length > 3) put('more', true)
      changes.push(`Times: ${andList(said.slice(0, 3))}${said.length > 3 ? ', and more' : ''}`)
    }
    if (type === 'oneSlot' && said.length) { put('time', said[0]); changes.push(`Time: ${said[0]}`) }
    if (f.more && type === 'freeSlots') put('more', true)
    if (type === 'oneSlot') {
      const svc = serviceFor(req.body || req.text) || cap((SERVICES.exec(req.body || req.text) || [])[0] || '')
      if (svc) {
        const onMenu = matchService(ctx.services, svc)
        const name = onMenu ? onMenu.name : svc
        put('service', name)
        changes.push(`For: ${lower(name)}`)
      }
    }
    // Times the salon didn't say, from their own diary
    if (!said.length && (fresh || f.date || req.fromDiary)) {
      const day = ctx.freeDay
      if (day && day.times && day.times.length) {
        if (!f.date && day.date) { put('date', day.date); changes.push(`Day: ${daySaid(day.date, now)}`) }
        // One stylist's free times are theirs: their name goes on the post
        const whose = day.who ? (day.mine ? 'Your free' : `${day.who.firstName}’s free`) : 'Free'
        const from = day.who ? 'the diary' : 'your diary'
        if (day.who) Object.assign(content, { staffId: day.who.id, staffName: day.who.firstName })
        if (type === 'freeSlots') {
          put('times', day.times.slice(0, 3))
          if ((day.count || day.times.length) > 3) put('more', true)
          changes.push(`${whose} times from ${from}: ${andList(day.times.slice(0, 3))}`)
        } else {
          put('time', day.times[0])
          changes.push(`${whose} time from ${from}: ${day.times[0]}`)
        }
      } else if (ctx.diaryRead) {
        const who = ctx.freeNamed ? `${ctx.freeNamed} has` : 'Your diary has'
        notes.push(f.date ? `${who} no gaps ${daySaid(f.date, now)}.` : `${who} no gaps this week.`)
      }
    }
  }

  if (type === 'team') {
    const who = f.name ? member(f.name) : null
    if (f.name) {
      const name = who ? firstName(who.name) : f.name
      put('name', name)
      changes.push(`Meet: ${name}`)
      if (f.role) { put('role', f.role); changes.push(`Role: ${lower(f.role)}`) } else if (who && who.role) { put('role', who.role); changes.push(`Role, from your team: ${lower(who.role)}`) }
      const cur = photos.main && photos.main.src
      if (who && who.image && (!cur || isStock(cur))) {
        photos = { ...photos, main: { src: who.image, fx: 0.5, fy: 0.35, zoom: 1 } }
        changes.push(`Photo: ${name}’s, from your team`)
      }
      if (who) source = `team:${who.id}`
    }
    if (f.line) { put('line', f.line); changes.push(`${label('line')}: “${f.line}”`) }
  }

  // A review post Bailey starts on its own words shares the newest kind review
  const pickReview = req.reviewPick || (fresh && !lockedReview && !words.main && !f.quote ? 'latest' : null)
  if (type === 'review' && (req.names.length || pickReview)) {
    const list = (ctx.reviews || []).filter(r => r && r.text)
    let r = null
    if (req.names.length) {
      const want = req.names[0].toLowerCase()
      const theirs = list.filter(x => firstName(x.firstName || x.author).toLowerCase() === want)
      r = theirs.find(kindReview) || theirs[0] || null
    } else if (pickReview === 'best') {
      r = list.filter(kindReview).reduce((best, x) => (!best || Number(x.rating) > Number(best.rating) ? x : best), null)
    } else r = list.find(kindReview) || null
    if (r) {
      const name = firstName(r.firstName || r.author) || 'A client'
      Object.assign(content, { quote: r.text, name, service: r.service || '', rating: Math.max(1, Math.min(5, Number(r.rating) || 5)) })
      ;['quote', 'name', 'service', 'rating'].forEach((k) => { touched[k] = true; given.add(k) })
      source = `review:${r.id}`
      changes.push(`${name}’s ${content.rating} star review, word for word`)
    } else if (!ctx.reviews) need('The review. I couldn’t read your reviews just now, so paste it in', 'quote')
    else need(req.names.length ? `A review from ${req.names[0]}. I couldn’t find one, so paste it in` : 'A four or five star review. There isn’t one yet, so paste one in', 'quote')
  }

  if (type === 'beforeAfter' || type === 'freshWork') {
    const svc = (SERVICES.exec(req.body || req.text) || [])[0]
    if ((svc || by) && !words.detail) {
      const detail = `${svc ? cap(svc.toLowerCase()) : 'Work'}${by ? ` by ${by}` : ''}`
      put('detail', detail)
      changes.push(`${label('detail')}: ${detail}`)
    }
    // A fresh post's headline says nothing about a photo it hasn't seen
    if (type === 'freshWork' && fresh && !given.has('headline')) {
      const line = linesFor('freshWork', 'headline', 'warm', next.trade || ctx.trade || 'hair', null)[0]
      if (line) { content.headline = line; touched.headline = true }
    }
  }

  if (type === 'priceList') {
    if (f.items) {
      put('items', f.items)
      changes.push(`Prices: ${f.items.map(i => `${i.name} ${i.price}`).join(', ')}`)
    } else if ((fresh || req.fromMenu || f.menuWords) && ctx.services && ctx.services.length) {
      const list = menuItems(ctx.services, f.menuWords)
      if (list.length) {
        put('items', list)
        const names = list.map(i => i.name)
        changes.push(`Prices from your menu: ${names.slice(0, 3).join(', ')}${names.length > 3 ? `, and ${names.length - 3} more` : ''}`)
      }
    } else if (req.fromMenu && ctx.services && !ctx.services.length) notes.push('Your menu has no prices yet.')
  }

  if (type === 'tips' && (f.tipBody || f.tipHead)) {
    if (f.tipHead && !words.small) { put('label', `A tip for ${lower(f.tipHead)}`); changes.push(`${label('label')}: A tip for ${lower(f.tipHead)}`) }
    if (f.tipBody) {
      const { headline, body } = splitTip(f.tipBody)
      if (!words.main) put('headline', headline)
      put('body', body)
      changes.push(`Tip: “${sentence(f.tipBody)}”`)
    }
  }
  if (type === 'question' && f.question) {
    if (!words.main) put('headline', f.question)
    changes.push(`Question: “${f.question}”`)
    if (f.answer) { put('body', f.answer); changes.push(`Answer: “${f.answer}”`) } else if (fresh) { content.body = ''; need('Your answer', 'body') }
  }

  // A fresh post keeps none of the template's example facts
  if (fresh) emptyFacts(type, { content, touched, photos, given, need })

  // Straight instructions about the look (the rest goes to the remix)
  const tune = { ...(next.tune || {}) }
  const t = req.tune || {}
  if (t.brand === 'logo') {
    if (!(ctx.kit && ctx.kit.logo)) need('Your logo. Add it in Marketing, Config and I’ll put it on your posts')
    else if (tune.brand !== 'logo') { tune.brand = 'logo'; changes.push('Using your logo') }
  }
  if (t.brand === 'name' && tune.brand === 'logo') { delete tune.brand; changes.push('Showing your salon’s name instead of the logo') }
  if (t.brand === 'none' && tune.brand !== 'none') { tune.brand = 'none'; changes.push('Took your salon’s name off') }
  if (t.hideCta === true && !tune.hideCta) { tune.hideCta = true; changes.push('Took the last line off') }
  if (t.hideCta === false && tune.hideCta) { delete tune.hideCta; changes.push('Put the last line back') }
  if (t.size) {
    const cur = TEXT_SIZES.indexOf(TEXT_SIZES.includes(Number(tune.size)) ? Number(tune.size) : 1)
    const to = Math.max(0, Math.min(TEXT_SIZES.length - 1, cur + t.size))
    if (to === cur) notes.push(t.size > 0 ? 'The words are as big as they go.' : 'The words are as small as they go.')
    else {
      if (TEXT_SIZES[to] === 1) delete tune.size
      else tune.size = TEXT_SIZES[to]
      changes.push(t.size > 0 ? 'Bigger words' : 'Smaller words')
    }
  }
  if (t.colours && t.colours.length) {
    const fam = next.family || 'editorial'
    const l = fam === 'layouts' && layoutOf(next.type, next.layout)
    const roleFam = l && l.ground === 'accent' ? 'layoutsBold' : l && l.ground === 'ink' ? 'layoutsDark' : fam
    const c = postTheme({ ...next, tune }, { kit: ctx.kit || {} }).c
    const asked = {}
    t.colours.forEach(({ role, hex, said }) => {
      asked[(ROLE_KEY[role] || ROLE_KEY.words)[roleFam] || 'paper'] = hex
      changes.push(`${ROLE_SAID[role] || 'Colour'}: ${said}`)
    })
    let full = { paper: c.paper, ink: c.ink, accent: c.accent, pop: c.pop, ...(fam === 'layouts' ? { on: c.on } : {}), ...asked }
    if (fam === 'poster' && asked.paper) full.pop = asked.paper
    if (hardToRead(fam, full).length) {
      const keys = Object.keys(asked)
      full = { ...full, ...fixColours(fam, full, keys[keys.length - 1]), ...asked }
      if (fam === 'poster' && asked.paper) full.pop = asked.paper
      notes.push(hardToRead(fam, full).length ? 'Those colours are hard to read together. Colours, Fix it can help.' : 'I moved the other colours a little so the words stay easy to read.')
    }
    tune.colours = { paper: full.paper, ink: full.ink, accent: full.accent, pop: full.pop, ...(fam === 'layouts' ? { on: full.on } : {}) }
  }
  if (t.filter) {
    const keys = Object.keys(photos).filter(k => photos[k] && photos[k].src)
    if (!def.photos.length) notes.push('This kind of post has no photo.')
    else if (!keys.length) need('A photo first, then I can change how it looks', `photo:${def.photos[0].key}`)
    else {
      photos = { ...photos }
      keys.forEach((k) => {
        const ph = { ...photos[k] }
        if (t.filter === 'none') delete ph.filter
        else ph.filter = t.filter
        photos[k] = ph
      })
      changes.push(FILTER_SAID[t.filter])
    }
  }

  // Story or feed post
  let format = next.format || 'post'
  if (req.format && req.format !== format) { format = req.format; changes.push(req.format === 'story' ? 'Now a Story' : 'Now a feed post') }

  // When to post it: said outright, or a day and time on a post whose days
  // aren't part of it ("share Hannah's review tomorrow at 9")
  let plannedAt = next.plannedAt || null
  let status = next.status || 'draft'
  const at = req.schedule ? moment(req.schedule.at) : req.when && !DAYS_ARE_CONTENT.includes(type) ? moment(req.when) : null
  if (at) {
    if (!at.isAfter(moment(now))) need('A later time to post it. That time has gone')
    else {
      plannedAt = at.toISOString()
      if (status !== 'posted') status = 'planned'
      changes.push(`Planned for ${whenSaid(at, now)}`)
    }
  }

  let out = { ...next, content, touched, photos, source, format, plannedAt, status, tune: Object.keys(tune).length ? tune : undefined }

  // The caption: written again when the words change (unless the salon wrote
  // it), in a new voice, or with hashtags added or taken off
  const old = next.caption
  const wordsChanged = given.size > 0 || switched || fresh
  let caption = old
  if (wordsChanged && !(old && old.edited)) caption = old && old.voice && old.voice !== 'warm' && ctx.captionFor ? ctx.captionFor(out, old.voice) : null
  const asks = req.caption || {}
  if ((asks.voice || asks.rewrite || asks.add || asks.clear) && ctx.captionFor) {
    const voice = asks.voice || (old && old.voice) || 'warm'
    let base = asks.voice || asks.rewrite ? ctx.captionFor(out, voice) : (caption || ctx.captionFor(out, voice))
    if (asks.voice) changes.push(`Caption: ${voice === 'fun' ? 'playful' : voice}`)
    else if (asks.rewrite) changes.push('Caption: written again')
    if (asks.clear) { base = { ...base, hashtags: [], edited: true }; changes.push('Took the hashtags off') }
    if (asks.add) { base = { ...base, hashtags: Array.from(new Set([...(base.hashtags || []), ...asks.add])), edited: true }; changes.push(`Added ${asks.add.join(' ')}`) }
    caption = base
  }
  out = { ...out, caption: caption || null }

  if (!changes.length && !fresh) return { post, changes, needs, notes, given: [], look: req.look, lookText: req.lookText }
  return { post: out, changes, needs, notes, given: Array.from(given), look: req.look, lookText: req.lookText }
}

// A new post from a sentence (the Social home's "Tell Bailey what to post"):
// the job's own layout, in the salon's brand colours when they have one
export const createFromRequest = (req, ctx = {}) => {
  const job = req.job && POST_TYPES[req.job] ? req.job : 'freshWork'
  const show = showcaseFor(job)
  const format = req.format || (job === 'freeSlots' || job === 'oneSlot' ? 'story' : 'post')
  const brandColour = ctx.kit && ctx.kit.accent
  const start = makePost(job, { trade: ctx.trade || 'hair', family: show.family, layout: show.layout, colourway: brandColour ? 'brand' : show.colourway, brandColour, format })
  const res = applyRequest(start, { ...req, job: null, format: null }, { ...ctx, fresh: true })
  return {
    ...res,
    post: { ...res.post, fromBailey: true },
    changes: [`Made ${JOB_SAID[job]}${format === 'story' ? ' as a Story' : ''}`, ...res.changes],
  }
}

// What Bailey says when a request wasn't understood
export const NOT_UNDERSTOOD = 'I didn’t catch that one. I can change the offer and prices, the day and times, who it’s about, any of the words, the colours, when it posts and the caption.'
export const NOT_UNDERSTOOD_HOME = 'I didn’t catch that one. Try “20% off colour this week”, “post my free slots” or “meet Jess, our new stylist”.'
